import { useState, type FormEvent } from 'react'
import {
  ANTHROPIC_API_KEY_PATTERN,
  ANTHROPIC_CHAT_MODEL_CHOICES,
  DEFAULT_ANTHROPIC_CHAT_MODEL,
  type AnthropicCredentialsUpdate,
  type AnthropicCredentialsView,
} from '@rp/contracts'
import { Field } from '../../design'
import { formatDateTime } from '../../lib/time'
import { AI_CREDENTIALS_COPY } from './aiCredentialsCopy'
import { CredentialsFormFooter } from './CredentialsFormFooter'

type AnthropicModel = AnthropicCredentialsUpdate['model']

export interface AnthropicCredentialsFormProps {
  current: AnthropicCredentialsView | null
  pending: boolean
  error: string | null
  onSubmit: (value: AnthropicCredentialsUpdate) => void
  onCancel?: () => void
}

const apiKeyHint = (current: AnthropicCredentialsView | null): string =>
  current === null
    ? AI_CREDENTIALS_COPY.apiKeyNew
    : AI_CREDENTIALS_COPY.apiKeyKept(formatDateTime(new Date(current.secretUpdatedAt)))

const initialModel = (current: AnthropicCredentialsView | null): AnthropicModel =>
  ANTHROPIC_CHAT_MODEL_CHOICES.find((choice) => choice === current?.model) ?? DEFAULT_ANTHROPIC_CHAT_MODEL

const keyErrorOf = (key: string, needsKey: boolean): string | null => {
  if (key === '') return needsKey ? AI_CREDENTIALS_COPY.invalid.apiKeyRequired : null
  return ANTHROPIC_API_KEY_PATTERN.test(key) ? null : AI_CREDENTIALS_COPY.invalid.apiKeyFormat
}

export const AnthropicCredentialsForm = ({ current, pending, error, onSubmit, onCancel }: AnthropicCredentialsFormProps) => {
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState<AnthropicModel>(initialModel(current))
  const [keyError, setKeyError] = useState<string | null>(null)

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const key = apiKey.trim()
    const found = keyErrorOf(key, current === null)
    setKeyError(found)
    if (found !== null) return
    onSubmit(key === '' ? { model } : { apiKey: key, model })
  }

  return (
    <form className="ai-credentials-form" onSubmit={onFormSubmit} noValidate>
      <Field id="anthropic-key" label={AI_CREDENTIALS_COPY.apiKey} hint={apiKeyHint(current)} {...(keyError === null ? {} : { error: keyError })}>
        {({ id, describedBy }) => (
          <input
            id={id}
            type="password"
            autoComplete="off"
            aria-describedby={describedBy}
            aria-invalid={keyError === null ? undefined : true}
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
          />
        )}
      </Field>
      <Field id="anthropic-model" label={AI_CREDENTIALS_COPY.anthropicModel}>
        {({ id }) => (
          <select id={id} value={model} onChange={(event) => setModel(event.target.value as AnthropicModel)}>
            {ANTHROPIC_CHAT_MODEL_CHOICES.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        )}
      </Field>
      <CredentialsFormFooter pending={pending} error={error} {...(onCancel === undefined ? {} : { onCancel })} />
    </form>
  )
}
